"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { ChevronDown } from "lucide-react";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { cn } from "@/lib/utils";
import { GROQHeader } from "@/sanity/lib/definitions";
import { IPageLink, resolvePageLink } from "@/sanity/globals/CTA";
import { CTAButton } from "@/shadcn/ui/button";
import { Motion } from "@/components/global/utils/Motion";
import LanguageSwitcher from "@/components/global/LanguageSwitcher";

import logo from "@/public/logo-black.svg";
import logoBeyond from "@/public/global/beyond-logo-green.svg";
import menuOpen from "@/public/global/svg/menu.svg";
import menuClose from "@/public/global/svg/close.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(useGSAP);
}

export default function NavBarV2({
  headerData,
  headerType = "default",
  animate = false,
  className,
}: {
  headerData: GROQHeader;
  headerType?: "default" | "shop" | "beyond";
  animate?: boolean;
  className?: string;
}) {
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );
  const [mobileMenuTL, setMobileMenuTL] = useState<gsap.core.Timeline | null>();

  const containerRef = useRef<HTMLDivElement | any>();
  const overlayMobileMenu = useRef<HTMLDivElement | any>();
  const mobileMenuItems = useRef<HTMLDivElement | any>();
  const mobileMenuBottomItems = useRef<HTMLDivElement | any>();

  const pathname = usePathname();

  //Left commented out to for now, doesnt need to get the anchors for menu
  // const [anchor, setAnchor] = useState<string | undefined>(undefined);
  // const params = useParams();

  // useEffect(() => {
  //   setAnchor(window.location.hash);
  // }, [params]);

  const lang =
    pathname.slice(-3) === "/de" || pathname === "/de"
      ? "de"
      : pathname.slice(-3) === "/br" || pathname === "/br"
        ? "br"
        : "en";

  function resolvePathname(pathname: string, lang: string) {
    if (
      (lang === "de" && pathname === "/de") ||
      (lang === "br" && pathname === "/br")
    )
      return "/";
    if (lang === "de" || lang === "br") return pathname.slice(0, -3);

    return pathname;
  }

  const addLanguageToLink = (link: string, lang: string) => {
    if (lang === "en") return link;

    if (link.startsWith("#")) {
      return lang === "de" ? `/de${link}` : `/br${link}`;
    }

    if (link === "/") {
      return lang === "de" ? "/de" : "/br";
    }

    if (link.includes("#")) {
      const [path, anchor] = link.split("#");
      if (path === "" || path === "/") {
        return lang === "de" ? `/de#${anchor}` : `/br#${anchor}`;
      }
      return lang === "de" ? `${path}/de#${anchor}` : `${path}/br#${anchor}`;
    }

    return lang === "de" ? `${link}/de` : `${link}/br`;
  };

  useEffect(() => {
    // Mobile menu
    gsap.set(containerRef.current, { visibility: "visible" });

    // @ts-ignore
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.in", duration: 0.3 },
        paused: true,
        onStart: () => {
          setMobileMenuIsOpen(true);
        },
        onReverseComplete: () => {
          setMobileMenuIsOpen(false);
        },
      });

      tl.from(mobileMenuItems.current.children, {
        y: "-1rem",
        delay: 0.1,
        // stagger: 0.1,
      }).from(mobileMenuBottomItems.current, {
        y: "-1rem",
      });

      if (mobileMenuIsOpen) {
        tl.progress(1);
      }

      setMobileMenuTL(tl);
    }, [containerRef]);

    return () => {
      // @ts-ignore
      if (ctx != null) {
        // @ts-ignore
        ctx.revert();
      }
    };
  }, []);

  useGSAP(
    () => {
      gsap.set(overlayMobileMenu.current, { visibility: "visible" });

      if (mobileMenuIsOpen) {
        gsap.to(overlayMobileMenu.current, {
          y: 0,
          ease: "power2.in",
          duration: 0.25,
        });

        mobileMenuTL?.play();
      } else {
        mobileMenuTL?.reverse();

        gsap.to(overlayMobileMenu.current, {
          y: "-100%",
          ease: "power2.in",
          duration: 0.2,
          delay: 0.3,
        });
      }
    },
    {
      scope: overlayMobileMenu,
      dependencies: [mobileMenuIsOpen, mobileMenuTL],
    },
  );

  const NavbarContent = () => (
    <>
      {/* Logo - Far Left */}
      <div
        className={`${headerType === "shop" ? "w-full py-[.5rem] lg:py-0" : "w-full py-[1.5rem] lg:py-0"} ${headerData?.menu?.items ? "" : "mx-auto lg:mx-0"}`}
      >
        <Link
          className=" relative z-[2] flex w-fit"
          href={lang === "de" ? "/de" : lang === "br" ? "/br" : "/"}
        >
          {headerType === "shop" ? (
            // <Image
            //   alt="Logo"
            //   src={logoBitbull}
            //   className="!w-[31.5rem] flex-shrink-0"
            // />
            <></>
          ) : (
            <Image
              alt="Logo"
              src={headerType && headerType === "beyond" ? logoBeyond : logo}
              className="w-[16.375rem]"
            />
          )}
        </Link>
      </div>

      {/* Navigation Items - Center */}
      <div className="hidden justify-center items-center h-full lg:flex w-full gap-[2rem] py-[1rem] lg:absolute lg:top-0 lg:left-1/2 lg:-translate-x-1/2">
        {headerData?.menu != null &&
          headerData?.menu?.items?.map((item, index) => {
            // if dropdown
            return item?.dropdownLinks != null &&
              item?.dropdownLinks.length > 0 ? (
              <div key={index} className="relative group">
                <button
                  onClick={() =>
                    setOpenDropdownIndex(
                      openDropdownIndex === index ? null : index,
                    )
                  }
                  className="flex items-center gap-2 text-[#B9B9B9] hover:text-black text-[2rem] font-[500] font-helvetica-neue leading-[120%] relative after:absolute after:bottom-0 after:left-0 after:content-[''] after:h-[0.125rem] after:bg-black after:transition-all duration-200 after:w-0 group-hover:after:w-full whitespace-nowrap"
                >
                  {lang === "de" && item.titleDE ? item.titleDE : item.title}
                  <ChevronDown
                    className={`w-[2rem] h-[2rem] stroke-[1.5] transition-transform duration-200 ${openDropdownIndex === index ? "rotate-180" : ""}`}
                  />
                </button>
                <div className="absolute left-0 top-full invisible mt-4 bg-white border opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 z-[5] rounded-[.75rem] backdrop-blur-[2rem] p-[.125rem] border-white/20">
                  <div className="flex flex-col rounded-[0.5rem] p-[0.625rem] min-w-[28rem] gap-[1rem]">
                    {item.dropdownLinks.map((dropdownItem, dropIndex) => {
                      return (
                        <Link
                          key={dropIndex}
                          href={addLanguageToLink(
                            resolvePageLink(dropdownItem?.link as IPageLink) ??
                              "/",
                            lang,
                          )}
                          className={`${resolvePathname(pathname, lang) === dropdownItem?.link?.internalLink?.settings?.slug.current ? "bg-[#E4F0E9] text-black" : "text-[#B9B9B9]"} flex items-center transition-colors duration-300 group gap-[.75rem] text-[1.75rem] px-[1rem] py-[1rem] font-helvetica-neue font-[500] rounded-[.5rem] font-geist hover:text-black hover:bg-[#E4F0E9]`}
                        >
                          {dropdownItem?.icon && (
                            <Image
                              src={dropdownItem?.icon}
                              alt="Icon"
                              className="opacity-60 w-[2rem] h-[2rem] min-w-[2rem] min-h-[2rem]"
                            />
                          )}
                          {lang === "de" && dropdownItem.titleDE
                            ? dropdownItem.titleDE
                            : dropdownItem.title}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={index}
                href={addLanguageToLink(
                  resolvePageLink(item?.link as IPageLink) ?? "/",
                  lang,
                )}
                className={`${resolvePathname(pathname, lang) === (item.link?.anchor ? item?.link?.internalLink?.settings?.slug.current + "#" + item.link.anchor : item?.link?.internalLink?.settings?.slug.current) ? "before:w-[200%] before:h-[200%] text-black" : "after:w-0 hover:after:w-full"} group relative overflow-hidden text-[#B9B9B9] text-[2rem] font-[500] font-helvetica-neue leading-[120%] after:absolute after:bottom-0 after:left-0 after:content-[''] after:h-[0.125rem] after:bg-[#E4F0E9] after:transition-all h-full max-h-[5.625rem] whitespace-nowrap px-[1.5rem] pt-[1.75rem] pb-[1.62rem] transition-all duration-100 flex flex-col items-center justify-center rounded-[1rem] before:absolute before:bottom-0 before:left-1/2 before:-translate-x-1/2 before:w-0 before:h-0 before:bg-[#E4F0E9] before:rounded-full before:transition-all before:duration-500 before:ease-out hover:before:w-[200%] hover:before:h-[200%] before:z-[-1]`}
              >
                <div className="flex relative z-10 justify-center items-center w-fit">
                  <div className="transition-all duration-300 group-hover:opacity-0 group-hover:-translate-y-8">
                    {lang === "de" && item.titleDE ? item.titleDE : item.title}
                  </div>
                  <div className="absolute text-black whitespace-nowrap opacity-0 transition-all duration-300 translate-y-8 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0">
                    {lang === "de" && item.titleDE ? item.titleDE : item.title}
                  </div>
                </div>
              </Link>
            );
          })}
      </div>

      {/* CTA Buttons - Far Right */}
      {headerType === "shop" ? (
        <div className="w-full hidden justify-end items-center lg:flex gap-[.75rem]">
          {/*<Image alt="Star" src={star} className="!w-[2.5rem] flex-shrink-0" />*/}
          <p className="mb-0 text-[1.75rem]">
            <strong className="font-medium">Rated:</strong> 4.9/5 von hunderten
            Tradern
          </p>
        </div>
      ) : headerType !== "beyond" ? (
        <div className="w-full hidden justify-end lg:flex -space-x-[.25rem] [&_button]:!py-[1.25rem] pb-1 gap-[1rem]">
          {/* language switcher */}
          <LanguageSwitcher lang={lang} />
          {/* end language switcher */}
          {headerData?.menu.navbarCTA && (
            <CTAButton
              data={headerData?.menu.navbarCTA}
              className="!w-fit"
              headerButton
            />
          )}
          {headerData?.menu.navbarCTA2 && (
            <CTAButton
              data={headerData?.menu.navbarCTA2}
              className="!w-fit"
              headerButton
            />
          )}
        </div>
      ) : null}
      {/* end desktop */}

      {/* mobile menu button */}
      {headerData?.menu?.items && (
        <div className="w-full flex justify-end items-center lg:hidden gap-[1rem]">
          {/* language switcher */}
          {headerType !== "shop" && <LanguageSwitcher lang={lang} />}
          {/* end language switcher */}

          <button
            onClick={() => setMobileMenuIsOpen(!mobileMenuIsOpen)}
            className=""
          >
            <Image
              alt="Menu"
              src={menuOpen}
              className="w-[4.625rem] h-[4.625rem]"
            />
          </button>
        </div>
      )}
      {/* end mobile menu button */}
    </>
  );

  return (
    <>
      <nav
        id="navbar"
        ref={containerRef}
        className={cn(
          "flex absolute z-40 justify-between items-end w-full lg:px-0 top-[4rem] start-0 px-[4rem] lg:top-[9rem]",
          className,
        )}
      >
        {animate ? (
          <Motion
            className="flex relative justify-between items-center mx-auto w-full max-w-[187.375rem]"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <NavbarContent />
          </Motion>
        ) : (
          <div className="flex relative justify-between items-center mx-auto w-full max-w-[187.375rem]">
            <NavbarContent />
          </div>
        )}
      </nav>

      {/* mobile menu */}
      <div
        ref={overlayMobileMenu}
        id="mobile-menu-overlay"
        className="fixed top-0 left-0 w-full h-full  bg-white p-[2rem] translate-y-[-100%] overflow-clip overflow-y-auto z-[9999]"
      >
        <div className="relative w-full h-full bg-gradient-to-b from-[#FBFBFB] from-[-34.16%] to-[#E6F2EB] to-[99.28%] pt-[2.25rem] px-[2.5rem] pb-[3.25rem] rounded-[2rem]">
          <div className="flex relative flex-col justify-between items-stretch w-full h-full pb-[1rem]">
            <div className="flex relative flex-col items-stretch w-full">
              <div className="flex relative justify-between items-center mx-auto w-full max-w-[145rem] rounded-[1.25rem] mb-[4.5rem]">
                <Link
                  href={lang === "de" ? "/de" : "/"}
                  className="w-full transition-opacity duration-300 ease-in-out hover:opacity-80"
                  onClick={() => setMobileMenuIsOpen(false)}
                >
                  <Image
                    className="w-[17.75rem] lg:w-[21.875rem]"
                    src={
                      headerType && headerType === "beyond" ? logoBeyond : logo
                    }
                    alt="Logo"
                  />
                </Link>

                <button
                  onClick={() => setMobileMenuIsOpen(!mobileMenuIsOpen)}
                  className=""
                >
                  <Image
                    alt="Close"
                    src={menuClose}
                    className="w-[4.625rem] h-[4.625rem] shrink-0"
                  />
                </button>
              </div>
              <ul
                ref={mobileMenuItems}
                className="flex flex-col justify-between gap-[3rem]"
              >
                {headerData?.menu?.items != null &&
                  headerData?.menu?.items?.length > 0 &&
                  headerData?.menu?.items?.map((item, index) => {
                    return (
                      <li
                        className="flex flex-col justify-start items-start w-full border-b border-black/10 pb-[2rem]"
                        key={index}
                      >
                        {item?.dropdownLinks != null &&
                        item?.dropdownLinks.length > 0 ? (
                          <>
                            <button
                              onClick={() =>
                                setOpenDropdownIndex(
                                  openDropdownIndex === index ? null : index,
                                )
                              }
                              className="flex items-center gap-[1rem] w-full text-[#B9B9B9] hover:text-black text-[2.25rem] font-[500] font-sf leading-[120%] relative after:absolute after:bottom-0 after:right-0 after:content-[''] after:h-[0.125rem] after:bg-black after:transition-all duration-200"
                            >
                              {lang === "de" && item.titleDE
                                ? item.titleDE
                                : item.title}
                              <ChevronDown
                                className={`w-[2.5rem] h-[2.5rem] stroke-[1.5] transition-transform duration-200 opacity-60 ${openDropdownIndex === index ? "rotate-180" : ""}`}
                              />
                            </button>
                            <div
                              className={`w-full flex flex-col gap-[3.25rem] overflow-hidden transition-all duration-200 ${openDropdownIndex === index ? "max-h-[500px] opacity-100 mt-[3.25rem]" : "max-h-0 opacity-0"}`}
                            >
                              {item.dropdownLinks.map(
                                (dropdownItem, dropIndex) => (
                                  <Link
                                    key={dropIndex}
                                    href={addLanguageToLink(
                                      resolvePageLink(
                                        dropdownItem?.link as IPageLink,
                                      ) ?? "/",
                                      lang,
                                    )}
                                    className={`${resolvePathname(pathname, lang) === dropdownItem?.link?.internalLink?.settings?.slug.current ? "text-black" : "text-[#B9B9B9]"} hover:text-black text-[2.25rem] font-[500] font-sf leading-[120%] flex items-center gap-[1rem]`}
                                    onClick={() => setMobileMenuIsOpen(false)}
                                  >
                                    {dropdownItem?.icon && (
                                      <Image
                                        src={dropdownItem?.icon}
                                        alt="Icon"
                                        className="opacity-60 w-[2.5rem] h-[2.5rem] min-w-[2.5rem] min-h-[2.5rem]"
                                      />
                                    )}
                                    {lang === "de" && dropdownItem.titleDE
                                      ? dropdownItem.titleDE
                                      : dropdownItem.title}
                                  </Link>
                                ),
                              )}
                            </div>
                          </>
                        ) : (
                          <Link
                            href={addLanguageToLink(
                              resolvePageLink(item?.link as IPageLink) ?? "/",
                              lang,
                            )}
                            // @ts-ignore
                            className={`${resolvePathname(pathname, lang) === item?.link?.internalLink?.settings?.slug?.current && pathname !== "/" ? "after:w-0 text-black" : "after:w-0 hover:after:w-full"} w-full text-[#B9B9B9] hover:text-black text-[2.25rem] font-[500] font-sf leading-[120%] relative after:absolute after:bottom-0 after:right-0 after:content-[''] after:h-[0.125rem] after:bg-black after:transition-all duration-200`}
                            onClick={() => setMobileMenuIsOpen(false)}
                          >
                            {lang === "de" && item.titleDE
                              ? item.titleDE
                              : item.title}
                          </Link>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </div>

            {headerType !== "shop" && (
              <div
                ref={mobileMenuBottomItems}
                className="w-full flex flex-col gap-[2rem] [&_*]:!w-full"
              >
                {headerData?.menu.navbarCTA && (
                  <CTAButton data={headerData?.menu.navbarCTA} />
                )}
                {headerData?.menu.navbarCTA2 && (
                  <CTAButton data={headerData?.menu.navbarCTA2} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* end mobile menu */}
    </>
  );
}
